import {
    parseBandwidthToBytesPerSecond,
    parseDataSizeToBytes,
    type AssetMemoryBlockName,
    type ParsedTarget
} from './AssetMemoryAnalyzer';
import type { LVGLSchemaService } from './LVGLSchemaService';
import { isXmlParseErrorElement } from '../../../services/xmlParseError';

export type AssetMemoryValidationMarker = {
    message: string;
    startLineNumber: number;
    startColumn: number;
    endLineNumber: number;
    endColumn: number;
};

type TextPosition = {
    lineNumber: number;
    column: number;
};

type TagLocation = {
    tagName: string;
    fullTag: string;
    startIndex: number;
};

const ASSET_TAGS_BY_BLOCK: Record<AssetMemoryBlockName, string[]> = {
    images: ['data', 'file'],
    fonts: ['tiny_ttf', 'bin']
};

/**
 * Returns only direct children, optionally filtered by tag name.
 * This keeps validation scoped to the LVGL block structure instead of matching nested descendants.
 */
const getDirectChildren = (element: Element, tagNames?: string[]): Element[] => {
    const normalizedNames: string[] | undefined = tagNames?.map((tagName: string) => tagName.toLowerCase());

    return Array.from(element.children).filter((child: Element) => {
        if (!normalizedNames) return true;
        return normalizedNames.includes(child.tagName.toLowerCase());
    });
};

/**
 * Converts a string offset into the 1-based line/column shape expected by editor markers.
 */
const getPositionAtIndex = (text: string, index: number): TextPosition => {
    const beforeIndex: string = text.substring(0, index);
    const lines: string[] = beforeIndex.split('\n');

    return {
        lineNumber: lines.length,
        column: lines[lines.length - 1].length + 1
    };
};

/**
 * Escapes a dynamic attribute name before it is embedded in a regular expression.
 */
const escapeRegExp = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * Indexes opening tags by tag name so DOM elements can be mapped back to source locations.
 * The validator uses this for semantic checks that the schema pass cannot express.
 */
const collectOpeningTagLocations = (text: string): Map<string, TagLocation[]> => {
    const locationsByName: Map<string, TagLocation[]> = new Map();
    const commentRegExp: RegExp = /<!--[\s\S]*?-->/g;
    const commentRanges: [number, number][] = [];
    let commentMatch: RegExpExecArray | null;

    while ((commentMatch = commentRegExp.exec(text)) !== null) {
        commentRanges.push([commentMatch.index, commentMatch.index + commentMatch[0].length]);
    }

    const isInsideComment = (index: number): boolean =>
        commentRanges.some(([start, end]) => index >= start && index < end);

    const tagRegExp: RegExp = /<([a-zA-Z][\w-]*)([\s\S]*?)>/g;
    let match: RegExpExecArray | null;

    while ((match = tagRegExp.exec(text)) !== null) {
        if (isInsideComment(match.index)) continue;

        const [fullTag, tagName] = match;
        const normalizedTagName: string = tagName.toLowerCase();
        const locations: TagLocation[] = locationsByName.get(normalizedTagName) ?? [];

        locations.push({
            tagName: normalizedTagName,
            fullTag,
            startIndex: match.index
        });
        locationsByName.set(normalizedTagName, locations);
    }

    return locationsByName;
};

/**
 * Consumes tag locations in document order, matching the order returned by DOM traversal.
 * This avoids recalculating text positions for each semantic validation rule.
 */
const createLocationConsumer =
    (locationsByName: Map<string, TagLocation[]>): ((element: Element) => TagLocation | undefined) =>
    (element: Element): TagLocation | undefined =>
        locationsByName.get(element.tagName.toLowerCase())?.shift();

/**
 * Creates an editor marker on a specific attribute when possible, otherwise on the tag name.
 * Missing locations fall back to the start of the document so validation can still report a message.
 */
const createMarker = (
    text: string,
    location: TagLocation | undefined,
    message: string,
    attributeName?: string
): AssetMemoryValidationMarker => {
    if (!location) {
        return {
            message,
            startLineNumber: 1,
            startColumn: 1,
            endLineNumber: 1,
            endColumn: 1
        };
    }

    if (attributeName) {
        const attributeMatch: RegExpMatchArray | null = location.fullTag.match(
            new RegExp(`\\b${escapeRegExp(attributeName)}\\s*=\\s*["'][^"']*["']`)
        );

        if (attributeMatch?.index !== undefined) {
            const start: TextPosition = getPositionAtIndex(text, location.startIndex + attributeMatch.index);
            const end: TextPosition = getPositionAtIndex(
                text,
                location.startIndex + attributeMatch.index + attributeName.length
            );

            return {
                message,
                startLineNumber: start.lineNumber,
                startColumn: start.column,
                endLineNumber: end.lineNumber,
                endColumn: end.column
            };
        }
    }

    const start: TextPosition = getPositionAtIndex(text, location.startIndex + 1);
    const end: TextPosition = getPositionAtIndex(text, location.startIndex + 1 + location.tagName.length);

    return {
        message,
        startLineNumber: start.lineNumber,
        startColumn: start.column,
        endLineNumber: end.lineNumber,
        endColumn: end.column
    };
};

/**
 * Parses the pipe-separated target syntax used by globals asset blocks.
 * Empty segments and duplicate target names are ignored.
 */
const parseTargetList = (targetValue: string): string[] =>
    Array.from(
        new Set(
            targetValue
                .split('|')
                .map((targetName: string) => targetName.trim())
                .filter(Boolean)
        )
    );

/**
 * Checks whether a parsed project target declares the named memory region.
 */
const targetHasMemory = (target: ParsedTarget, memoryName: string): boolean =>
    target.memories.some((memory) => memory.name === memoryName);

/**
 * Validates project.xml declarations that depend on cross-element context:
 * duplicate targets, duplicate memories per target, and parseable size/bandwidth values.
 */
const validateProjectMemoryDeclarations = (
    text: string,
    rootElement: Element,
    getLocation: (element: Element) => TagLocation | undefined
): AssetMemoryValidationMarker[] => {
    const markers: AssetMemoryValidationMarker[] = [];
    const seenTargets: Set<string> = new Set();

    const targetsElements: Element[] = getDirectChildren(rootElement, ['targets']);

    if (targetsElements.length === 0) {
        markers.push(
            createMarker(
                text,
                getLocation(rootElement),
                `<project> must contain a <targets> block with at least one <target>.`
            )
        );
    } else {
        for (const targetsEl of targetsElements) {
            if (getDirectChildren(targetsEl, ['target']).length === 0) {
                markers.push(
                    createMarker(text, getLocation(targetsEl), `<targets> must contain at least one <target>.`)
                );
            }
        }
    }

    for (const target of targetsElements.flatMap((targets: Element) => getDirectChildren(targets, ['target']))) {
        const targetLocation: TagLocation | undefined = getLocation(target);
        const targetName: string = target.getAttribute('name')?.trim() ?? '';

        if (targetName) {
            if (seenTargets.has(targetName)) {
                markers.push(
                    createMarker(text, targetLocation, `Target "${targetName}" is declared more than once.`, 'name')
                );
            }
            seenTargets.add(targetName);
        }

        const seenMemories: Set<string> = new Set();
        for (const memory of getDirectChildren(target, ['memory'])) {
            const memoryLocation: TagLocation | undefined = getLocation(memory);
            const memoryName: string = memory.getAttribute('name')?.trim() ?? '';
            const size: string | null = memory.getAttribute('size');
            const bandwidth: string | null = memory.getAttribute('bandwidth');

            if (memoryName) {
                if (seenMemories.has(memoryName)) {
                    markers.push(
                        createMarker(
                            text,
                            memoryLocation,
                            `Memory region "${memoryName}" is declared more than once in target "${targetName}".`,
                            'name'
                        )
                    );
                }
                seenMemories.add(memoryName);
            }

            if (size !== null && parseDataSizeToBytes(size) === null) {
                markers.push(
                    createMarker(
                        text,
                        memoryLocation,
                        `Memory region "${memoryName}" in target "${targetName}" has invalid size "${size.trim()}".`,
                        'size'
                    )
                );
            }

            if (bandwidth !== null && parseBandwidthToBytesPerSecond(bandwidth) === null) {
                markers.push(
                    createMarker(
                        text,
                        memoryLocation,
                        `Memory region "${memoryName}" in target "${targetName}" has invalid bandwidth "${bandwidth.trim()}".`,
                        'bandwidth'
                    )
                );
            }
        }
    }

    return markers;
};

/**
 * Validates the block-level memory assignment rules for one <images> or <fonts> block.
 * A single target is assumed; multiple targets require target only when memory is assigned.
 */
const validateGlobalsAssetBlock = (
    text: string,
    block: Element,
    blockName: AssetMemoryBlockName,
    projectTargets: ParsedTarget[],
    location: TagLocation | undefined
): AssetMemoryValidationMarker[] => {
    const markers: AssetMemoryValidationMarker[] = [];
    const targetAttr: string | null = block.getAttribute('if_target');
    const memoryAttr: string | null = block.getAttribute('memory');
    const memoryName: string = memoryAttr?.trim() ?? '';
    const hasAssets: boolean = getDirectChildren(block, ASSET_TAGS_BY_BLOCK[blockName]).length > 0;
    const hasMemoryAssignment: boolean = memoryName.length > 0;
    const shouldValidateAssignment: boolean = hasAssets || targetAttr !== null || hasMemoryAssignment;

    if (!shouldValidateAssignment) return markers;

    const targetsByName: Map<string, ParsedTarget> = new Map(
        projectTargets.map((target: ParsedTarget) => [target.name, target])
    );
    let selectedTargets: ParsedTarget[] = [];

    if (targetAttr !== null) {
        const targetNames: string[] = parseTargetList(targetAttr);

        if (targetNames.length === 0) {
            markers.push(
                createMarker(text, location, `<${blockName}> has an empty "if_target" attribute.`, 'if_target')
            );
        } else if (targetsByName.size > 0) {
            const missingTargetNames: string[] = targetNames.filter(
                (targetName: string) => !targetsByName.has(targetName)
            );

            if (missingTargetNames.length > 0) {
                markers.push(
                    createMarker(
                        text,
                        location,
                        `<${blockName}> references unknown target(s): ${missingTargetNames.join(', ')}.`,
                        'if_target'
                    )
                );
            }

            selectedTargets = targetNames
                .map((targetName: string) => targetsByName.get(targetName))
                .filter((target: ParsedTarget | undefined): target is ParsedTarget => Boolean(target));
        }
    } else if (projectTargets.length === 1) {
        selectedTargets = projectTargets;
    } else if (projectTargets.length > 1 && hasMemoryAssignment) {
        markers.push(
            createMarker(
                text,
                location,
                `<${blockName}> must specify target when project.xml defines multiple targets.`,
                'if_target'
            )
        );
    }

    if (!memoryName) return markers;

    if (selectedTargets.length > 0) {
        const missingMemoryTargets: string[] = selectedTargets
            .filter((target: ParsedTarget) => !targetHasMemory(target, memoryName))
            .map((target: ParsedTarget) => target.name);

        if (missingMemoryTargets.length > 0) {
            markers.push(
                createMarker(
                    text,
                    location,
                    `<${blockName}> references memory "${memoryName}", but it does not exist in target(s): ${missingMemoryTargets.join(', ')}.`,
                    'memory'
                )
            );
        }
    }

    return markers;
};

/**
 * Applies globals.xml asset memory validation to each top-level <images> and <fonts> block.
 * Project target data is read from LVGLSchemaService, which is populated from project.xml.
 */
const validateGlobalsAssetMemoryAssignments = (
    text: string,
    rootElement: Element,
    schemaService: LVGLSchemaService,
    getLocation: (element: Element) => TagLocation | undefined
): AssetMemoryValidationMarker[] => {
    const markers: AssetMemoryValidationMarker[] = [];
    const projectTargets: ParsedTarget[] = schemaService.projectTargets ?? [];

    for (const block of getDirectChildren(rootElement, ['images', 'fonts'])) {
        const blockName: AssetMemoryBlockName = block.tagName.toLowerCase() as AssetMemoryBlockName;

        markers.push(...validateGlobalsAssetBlock(text, block, blockName, projectTargets, getLocation(block)));
    }

    return markers;
};

/**
 * Entry point for semantic memory validation.
 * Schema validation still owns element/attribute shape; this handles rules that need project/global context.
 */
export const validateAssetMemoryXml = (
    text: string,
    parsedDocument: Document,
    schemaService: LVGLSchemaService
): AssetMemoryValidationMarker[] => {
    const rootElement: Element | null = parsedDocument.documentElement;
    if (!rootElement || isXmlParseErrorElement(rootElement)) return [];

    const locationsByName: Map<string, TagLocation[]> = collectOpeningTagLocations(text);
    const getLocation: (element: Element) => TagLocation | undefined = createLocationConsumer(locationsByName);
    const rootName: string = rootElement.tagName.toLowerCase();

    if (rootName === 'project') return validateProjectMemoryDeclarations(text, rootElement, getLocation);
    if (rootName === 'globals')
        return validateGlobalsAssetMemoryAssignments(text, rootElement, schemaService, getLocation);

    return [];
};
